import { useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { api } from '../../api/client.js'

/**
 * « Mot de passe oublié », depuis l'écran de connexion.
 *
 * ⭐ La boutique voyage avec la demande : un compte d'ÉQUIPE vit dans le schéma
 * de son enseigne, et la même adresse peut désigner deux personnes différentes
 * dans deux boutiques. Sans elle, le serveur ne saurait pas à qui écrire.
 *
 * ⚠️ La confirmation est la MÊME que l'adresse existe ou non, et c'est
 * délibéré : un message qui dirait « compte inconnu » transformerait ce
 * formulaire public en annuaire du personnel, à raison d'une adresse essayée
 * par seconde. Le serveur répond d'ailleurs pareil dans les deux cas — l'écran
 * ne fait ici que ne pas trahir ce que le serveur tait.
 */
export function OubliMotDePasseDialog({
  boutique,
  emailInitial,
  onClose,
}: {
  /** Slug de la boutique quand on entre par `/s/:slug/login`. */
  boutique: string | undefined
  emailInitial: string
  onClose: () => void
}): JSX.Element {
  const [email, setEmail] = useState(emailInitial)
  const [envoi, setEnvoi] = useState(false)
  const [fait, setFait] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  async function demander(): Promise<void> {
    setEnvoi(true)
    setErreur(null)
    try {
      await api.post('/auth/password/oubli', {
        email: email.trim(),
        ...(boutique ? { storeSlug: boutique } : {}),
      })
      setFait(true)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Demande impossible.')
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Mot de passe oublié</DialogTitle>
      <DialogContent>
        {fait ? (
          <Alert severity="success">
            Si un compte porte cette adresse, un lien vient d’y être envoyé. Il est valable une
            heure et ne sert qu’une fois.
          </Alert>
        ) : (
          <>
            {erreur && (
              <Alert severity="error" sx={{ mb: 3 }} role="alert">
                {erreur}
              </Alert>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Indiquez votre adresse : vous recevrez un lien pour choisir un nouveau mot de passe.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              type="email"
              label="Adresse e-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && email.trim() !== '') void demander()
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 6, py: 4 }}>
        <Button onClick={onClose}>{fait ? 'Fermer' : 'Annuler'}</Button>
        {!fait && (
          <Button
            variant="contained"
            disabled={envoi || email.trim() === ''}
            onClick={() => void demander()}
          >
            Envoyer le lien
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
